import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FileText, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../../styles/ThemeStyles';
import { useGlobalOrderData } from '../../../hooks/useGlobalOrderData';
import { validateOrderForPreview, getPreviewButtonValidationMessage } from '../../../utils/orderValidation';

const PreviewButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.lg};
`;

const PreviewButton = styled.button<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.lg} ${Spacing.xl};
  background: ${props => props.disabled 
    ? ThemeColors.gray[400] 
    : `linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%)`};
  color: white;
  border: none;
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: ${props => props.disabled ? 'none' : Shadows.md};
  width: 100%;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${Shadows.lg};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ValidationMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  background-color: ${ThemeColors.warning.light};
  color: ${ThemeColors.warning.dark};
  border: 1px solid ${ThemeColors.warning.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  text-align: center;
  margin-top: ${Spacing.xs};
`;

const CustOrderNote = styled.div`
  padding: ${Spacing.md};
  background-color: ${ThemeColors.info.light};
  color: ${ThemeColors.info.dark};
  border: 1px solid ${ThemeColors.info.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  text-align: center;
  margin-top: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
`;

interface PreviewOrderButtonProps {
  onPreview: () => void;
  className?: string;
  /** Type of order - 'store-order' shows only product items, 'supplies' shows only supply items */
  orderType?: 'store-order' | 'supplies';
}

export const PreviewOrderButton: React.FC<PreviewOrderButtonProps> = ({ onPreview, className, orderType = 'store-order' }) => {
  const { orderData, isLoading } = useGlobalOrderData();
  const { t } = useTranslation();
  
  // Memoize calculations to avoid unnecessary re-renders and loops
  // Filter items based on orderType
  const { totalItems, totalAmount } = useMemo(() => {
    let items: any[] = [];
    
    if (orderType === 'supplies') {
      // For supplies, only count supply items
      items = [...(orderData.supplyItems || [])];
    } else {
      // For store orders, only count product items (exclude supplies)
      items = [
        ...(orderData.heritageItems || []),
        ...(orderData.livingstonItems || []),
        ...(orderData.flowerItems || []),
        ...(orderData.vegetableItems || [])
      ];
    }
    
    const itemsTotal = items.reduce((sum, item) => sum + item.quantity, 0);
    const amountTotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return { totalItems: itemsTotal, totalAmount: amountTotal };
  }, [orderType, orderData.heritageItems, orderData.livingstonItems, orderData.flowerItems, orderData.vegetableItems, orderData.supplyItems]);
  
  // Memoize validation to avoid recalculating on every render
  // Only recalculate when relevant fields change to prevent loops
  // Use stable references for product arrays (lengths) instead of the arrays themselves
  const productArraysKey = useMemo(() => {
    if (orderType === 'supplies') {
      return [
        orderData.supplyItems?.length || 0,
        totalItems
      ].join(',');
    } else {
      return [
        orderData.heritageItems?.length || 0,
        orderData.livingstonItems?.length || 0,
        orderData.flowerItems?.length || 0,
        orderData.vegetableItems?.length || 0,
        totalItems
      ].join(',');
    }
  }, [
    orderType,
    orderData.heritageItems?.length,
    orderData.livingstonItems?.length,
    orderData.flowerItems?.length,
    orderData.vegetableItems?.length,
    orderData.supplyItems?.length,
    totalItems
  ]);
  
  // Create filtered order data for validation based on orderType
  const filteredOrderDataForValidation = useMemo(() => {
    if (orderType === 'supplies') {
      return {
        ...orderData,
        heritageItems: [],
        livingstonItems: [],
        flowerItems: [],
        vegetableItems: []
      };
    } else {
      return {
        ...orderData,
        supplyItems: []
      };
    }
  }, [orderData, orderType]);
  
  const validation = useMemo(() => {
    return validateOrderForPreview(filteredOrderDataForValidation, orderType);
  }, [
    filteredOrderDataForValidation.poNumber,
    filteredOrderDataForValidation.deliveryDate,
    filteredOrderDataForValidation.orderType,
    productArraysKey, // Stable key based on product array lengths and total items
    orderType // Include orderType to re-validate when switching between store-order and supplies
  ]);
  
  // Memoize validation message based on validation result
  const validationMessage = useMemo(() => {
    return validation.message || '';
  }, [validation.message]);

  // Use useCallback to prevent function recreation on every render
  const handlePreviewClick = useCallback(() => {
    if (validation.isValid && !isLoading) {
      onPreview();
    }
  }, [validation.isValid, isLoading, onPreview]);

  // Check if Cust Order notice should be shown (only for store-order, not supplies)
  const showCustOrderNotice = orderType === 'store-order' && 
                               orderData.orderType === 'custOrder' && 
                               totalItems > 0 && 
                               totalItems < 150;

  return (
    <PreviewButtonContainer className={className}>
      {/* Show Cust Order minimum requirement notice */}
      {showCustOrderNotice && (
        <CustOrderNote>
          <AlertCircle size={18} />
          <span>
            Cust Order requires a minimum of 150 items. Current total: {totalItems} items ({150 - totalItems} more needed)
          </span>
        </CustOrderNote>
      )}

      <PreviewButton
        onClick={handlePreviewClick}
        disabled={!validation.isValid || isLoading}
      >
        <FileText size={20} />
        {`Preview Order (${totalItems} items - $${totalAmount.toFixed(2)})`}
      </PreviewButton>
      
      {/* Show validation messages */}
      {!validation.isValid && validationMessage && (
        <ValidationMessage>
          <AlertCircle size={18} />
          {validationMessage}
        </ValidationMessage>
      )}
    </PreviewButtonContainer>
  );
};

